<template>
  <SupportPageCard
    page-class="work-temp-page"
    :title="pageTitle"
    :show-back="!hideBackBtn"
    @back="goBack"
  >
    <van-form
      v-if="!visibleLoading"
      class="form work-temp-form"
      ref="formRef"
      @submit="onSubmit"
    >
      <TempDateTimePicker
        id="registration_date"
        :formData="formData"
        :labelInfo="registerDateLabel"
        :customKey="{ type: 'year-month' }"
        :required="true"
        :label="languages.selfServiceRegisterDate"
        :placeholder="languages.smartOrderPleaseChoose + languages.selfServiceRegisterDate"
      />
      <p v-if="fieldErrors.registration_date" class="tip">
        {{ fieldErrors.registration_date }}
      </p>
      <TempSelect
        v-if="socialOptions.length"
        id="social_type"
        :formData="formData"
        :labelInfo="socialTypeLabel"
        :customKey="{ options: socialOptions }"
        :required="true"
        :label="languages.selfServiceSocialType"
        :placeholder="languages.smartOrderPleaseChoose + languages.selfServiceSocialType"
      />
      <TempInput
        v-else
        id="social_type"
        type="text"
        :formData="formData"
        :labelInfo="socialTypeLabel"
        :required="true"
        :label="languages.selfServiceSocialType"
        :placeholder="languages.smartOrderPleaseInpt + languages.selfServiceSocialType"
      />
      <p v-if="fieldErrors.social_type" class="tip">
        {{ fieldErrors.social_type }}
      </p>
      <TempSelect
        id="device_type"
        :formData="formData"
        :labelInfo="deviceTypeLabel"
        :customKey="{ options: deviceTypeOptions, defaultValue: 'device_id' }"
        :required="true"
        :label="languages.selfServiceDeviceType"
        :placeholder="languages.smartOrderPleaseChoose + languages.selfServiceDeviceType"
      />
      <TempInput
        id="device"
        type="text"
        :formData="formData"
        :labelInfo="deviceLabel"
        :required="true"
        :label="deviceLabelText"
        :placeholder="languages.smartOrderPleaseInpt + deviceLabelText"
      />
      <p v-if="fieldErrors.device" class="tip">
        {{ fieldErrors.device }}
      </p>
      <template v-if="showPayOrder">
        <TempInput
          id="pay_order_id"
          type="text"
          :formData="formData"
          :labelInfo="payOrderLabel"
          :required="false"
          :label="languages.selfServicePayOrder"
          :placeholder="languages.smartOrderPleaseInpt + languages.selfServicePayOrder"
        />
        <p v-if="fieldErrors.pay_order_id" class="tip">
          {{ fieldErrors.pay_order_id }}
        </p>
        <TempSelect
          id="pay_channel"
          :formData="formData"
          :labelInfo="payChannelLabel"
          :customKey="{ options: payChannelOptions, defaultValue: 'google' }"
          :required="false"
          :label="languages.selfServicePayChannel"
          :placeholder="languages.smartOrderPleaseChoose + languages.selfServicePayChannel"
        />
      </template>
    </van-form>

    <template v-if="!visibleLoading" #footer>
      <div class="form-item-submit">
        <van-button
          class="btn"
          block
          type="info"
          :loading="loadingSubmit"
          @click="onFooterSubmit"
        >
          <template #icon>
            <em class="btn-icon iconfont icon-info"></em>
          </template>
          <template>{{ languages.submit }}</template>
        </van-button>
      </div>
    </template>

    <template #overlay>
      <van-overlay :show="visibleLoading">
        <div class="loading-wrapper" @click.stop>
          <van-loading color="#1989fa" size="40" />
        </div>
      </van-overlay>
    </template>
  </SupportPageCard>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import SupportPageCard from '@components/officialSupport/common/SupportPageCard.vue';
import TempSelect from '@components/officialSupport/workTemp/TempSelect.vue';
import TempInput from '@components/officialSupport/workTemp/TempInput.vue';
import TempDateTimePicker from '@components/officialSupport/workTemp/TempDateTimePicker.vue';
import iframeMessage from '@mixins/officialSupport/iframeMessage';
import globalConfig from '@utiles/globalConfig';

const hideBackBtn = globalConfig.getHrefParam('hideBackBtn') == 1;
const DEBIND_SMART_TYPE = 'smartTypeDebindAccount';
const SUCCESS_CODES = ['13001', 'AccountUnBindingV2Ok'];

export default {
  name: 'SelfServiceFixed',
  mixins: [iframeMessage],
  components: {
    SupportPageCard,
    TempSelect,
    TempInput,
    TempDateTimePicker
  },
  data() {
    return {
      loadingSubmit: false,
      visibleLoading: true,
      hideBackBtn,
      showPayOrder: false,
      socialOptions: [],
      fieldErrors: {
        registration_date: '',
        social_type: '',
        device: '',
        pay_order_id: ''
      },
      formData: {
        registration_date: '',
        social_type: '',
        device_type: 'device_id',
        device: '',
        pay_order_id: '',
        pay_channel: 'google'
      }
    };
  },
  computed: {
    ...mapState({
      languages: state => state.languages,
      curLang: state => state.curLang,
      loginAccount: state => state.loginAccount,
      isIframe: state => state.isIframe,
      urlParams: state => state.urlParams
    }),
    smartType() {
      return this.$route.query.smartType || DEBIND_SMART_TYPE;
    },
    pageTitle() {
      return (
        this.$route.query.name ||
        this.languages.debindText ||
        this.languages.workHomeSelfService ||
        ''
      );
    },
    registerDateLabel() {
      return this.makeLabel(this.languages.selfServiceRegisterDate, true);
    },
    socialTypeLabel() {
      return this.makeLabel(this.languages.selfServiceSocialType, true);
    },
    deviceTypeLabel() {
      return this.makeLabel(this.languages.selfServiceDeviceType, true);
    },
    deviceLabelText() {
      return this.formData.device_type === 'phone_model'
        ? this.languages.selfServicePhoneModel
        : this.languages.selfServiceDeviceId;
    },
    deviceLabel() {
      return this.makeLabel(this.deviceLabelText, true);
    },
    payOrderLabel() {
      return this.makeLabel(this.languages.selfServicePayOrder, false);
    },
    payChannelLabel() {
      return this.makeLabel(this.languages.selfServicePayChannel, false);
    },
    deviceTypeOptions() {
      return [
        {
          label: this.languages.selfServiceDeviceId,
          value: 'device_id'
        },
        {
          label: this.languages.selfServicePhoneModel,
          value: 'phone_model'
        }
      ];
    },
    payChannelOptions() {
      return [
        { label: this.languages.googleOption || 'Google Play', value: 'google' },
        { label: this.languages.appleOption || 'App Store', value: 'apple' },
        { label: this.languages.otherOption || 'other', value: 'other' }
      ];
    }
  },
  watch: {
    curLang() {
      this.initPage();
    }
  },
  created() {
    this.initPage();
  },
  mounted() {
    this.scrollSsrPageToTop();
  },
  methods: {
    ...mapActions(['checkAccountPayInfo', 'submitFixedSelfService']),
    /** 进页将 #ssr-page 滚到顶部（与 workfaqdetail 一致） */
    scrollSsrPageToTop() {
      this.$nextTick(() => {
        const el = document.getElementById('ssr-page');
        if (el) el.scrollTop = 0;
      });
    },
    makeLabel(label, required) {
      return {
        label: label || '',
        tip: '',
        required: !!required,
        tipImg: []
      };
    },
    toSocialType(item) {
      const name = String(
        item?.SnsTypeName || item?.sns_type_name || item?.snsTypeName || ''
      ).trim();
      return name.toLowerCase();
    },
    async initPage() {
      if (this.smartType !== DEBIND_SMART_TYPE) {
        this.$toast(this.languages.smartOrderComing);
        this.goBack();
        return;
      }
      this.visibleLoading = true;
      try {
        const res = await this.checkAccountPayInfo({
          account: this.loginAccount,
          appLanguage: this.curLang,
          game_id: this.urlParams.gameId
        });
        if (res?.result === 1) {
          const data = res.data || {};
          const snsList = data.sns_list || data.snsList || [];
          this.socialOptions = snsList
            .map(item => {
              const value = this.toSocialType(item);
              if (!value) {
                return null;
              }
              return {
                label: item.SnsTypeName || item.sns_type_name || value,
                value
              };
            })
            .filter(Boolean);
          this.showPayOrder =
            Number(data.pay_order_count || data.payOrderCount || 0) > 0;
        }
      } catch (e) {
        // 预取失败仍展示表单，由提交接口校验
      }
      this.visibleLoading = false;
      this.scrollSsrPageToTop();
    },
    onFooterSubmit() {
      this.$refs.formRef && this.$refs.formRef.submit();
    },
    clearFieldErrors() {
      this.fieldErrors = {
        registration_date: '',
        social_type: '',
        device: '',
        pay_order_id: ''
      };
    },
    applyFieldError(msg) {
      const map = {
        '13002_1': 'registration_date',
        '13002_2': 'social_type',
        '13002_3': 'device',
        '13002_4': 'pay_order_id'
      };
      const key = map[msg];
      if (!key) {
        return;
      }
      const textMap = {
        registration_date: this.languages.registerTimeError,
        social_type: this.languages.itemWrong,
        device: this.languages.deviceModelError,
        pay_order_id: this.languages.itemWrong
      };
      this.fieldErrors[key] = textMap[key] || this.languages.itemWrong;
    },
    resolveMsg(msg) {
      if (!msg) {
        return '';
      }
      if (msg === '13002_1') {
        return this.languages.registerTimeError || msg;
      }
      if (msg === '13002_2' || msg === '13002_4') {
        return this.languages.itemWrong || msg;
      }
      if (msg === '13002_3') {
        return this.languages.deviceModelError || msg;
      }
      return this.languages[msg] || msg;
    },
    async onSubmit() {
      if (this.loadingSubmit) {
        return;
      }
      this.clearFieldErrors();
      this.loadingSubmit = true;
      const payload = {
        smart_type: DEBIND_SMART_TYPE,
        game_id: Number(this.urlParams.gameId),
        registration_date: this.formData.registration_date,
        social_type: this.formData.social_type,
        device_type: this.formData.device_type || 'device_id',
        device: this.formData.device
      };
      const orderId = String(this.formData.pay_order_id || '').trim();
      if (this.showPayOrder && orderId) {
        payload.pay_order_id_list = [
          {
            orderId,
            channel: this.formData.pay_channel || 'google'
          }
        ];
      }
      try {
        const res = await this.submitFixedSelfService(payload);
        const msg = res?.msg || res?.data?.msg_code || '';
        if (
          res?.result === 1 &&
          (SUCCESS_CODES.includes(msg) || SUCCESS_CODES.includes(res?.data?.msg_code))
        ) {
          this.$toast(this.languages.unbindSucc);
          this.$router.push('/workhome');
          return;
        }
        if (res?.result === 1) {
          this.$toast(this.languages.unbindSucc);
          this.$router.push('/workhome');
          return;
        }
        this.applyFieldError(msg);
        this.$toast(this.resolveMsg(msg) || this.languages.unbindFail);
      } catch (e) {
        this.$toast(this.languages.unbindFail);
      }
      this.loadingSubmit = false;
    },
    goBack() {
      if (this.isIframe) {
        this.postMessageToParent('goBack');
      } else if (globalConfig.isTiktokMiniProgram()) {
        this.$router.push('/workhome');
      } else {
        this.$router.go(-1);
      }
    }
  }
};
</script>
